/**
 * Moves the character's collision box through the solids with substeps,
 * resolving overlap per axis (y first, then x). The box is `2 * radius` wide
 * and `height` tall with its bottom at the feet. It is drawn as a capsule.
 */

import { abs } from "./fastmath";
import type { SimState } from "./state";
import type { CompiledTuning } from "./tuning";
import { SUB_DT, SUBSTEPS } from "./types";
import type { World } from "./world";

/** Result flags of the last move, written into these module fields (no allocation). */
export const moveResult = {
  /** 1 if the bottom of the box was pushed up onto a solid this move. */
  landed: 0,
  /** Vertical speed just before landing (positive = downward). */
  impactSpeed: 0,
  /** 1 if the head hit a ceiling. */
  headHit: 0,
  /** 1 if a wall stopped horizontal motion. */
  wallHit: 0,
  /** Corner corrections applied this move. */
  cornerNudges: 0,
  /** Solid that stopped horizontal motion (-1 none) and the horizontal speed it stopped. */
  wallSolid: -1,
  wallSpeed: 0,
};

export function capsuleHeight(t: CompiledTuning, tuck: number): number {
  return t.standHeight - t.crouchDrop * tuck;
}

/**
 * Integrate position over one tick with the current velocity, resolving
 * collisions in SUBSTEPS substeps. Velocity components are zeroed on impact.
 * `shapeDy` is the feet's extra rise this tick from a change of shape with the
 * mass held still (a tuck in the air lifts the feet, an extension lowers them);
 * it moves with the first substep, so feet extending onto a surface land on it.
 */
export function moveBody(world: World, s: SimState, t: CompiledTuning, shapeDy = 0): void {
  moveResult.landed = 0;
  moveResult.impactSpeed = 0;
  moveResult.headHit = 0;
  moveResult.wallHit = 0;
  moveResult.cornerNudges = 0;
  moveResult.wallSolid = -1;
  moveResult.wallSpeed = 0;

  const r = t.capsuleRadius;
  let h = capsuleHeight(t, s.tuck);
  // A vault's box is passed by the lower body while the hands hold it (plantSkip).
  const skip = s.plantHands === 1 && s.plantBox >= 0 && s.planted !== 3 ? s.plantBox : -1;

  for (let sub = 0; sub < SUBSTEPS; sub++) {
    // Vertical: the feet move with the mass's velocity, plus the whole change of shape on the first
    // substep, where the box already has its new height (at most tuckComDrop over tuckOutMs per tick).
    const dy = s.vy * SUB_DT + (sub === 0 ? shapeDy : 0);
    if (dy !== 0) {
      const vyBefore = s.vy;
      const feetBefore = s.y;
      s.y += dy;
      let guard = 0;
      while (guard++ < 4) {
        const i = world.overlapSolid(s.x - r, s.y, s.x + r, s.y + h, skip);
        if (i < 0) break;
        if (dy < 0 && feetBefore < world.solidY1[i]! - 1e-6) {
          // Moving down but the solid is not under the feet: the head grew into it (an extension
          // under a ceiling); push the body back under it. If that drives the feet into a floor, the
          // body does not fit between the two: keep the feet on the floor and crouch to the gap
          // instead, so the box never ends a move inside a solid.
          const under = world.solidY0[i]! - h;
          const floor = world.probeDown(s.x - r, s.x + r, feetBefore, feetBefore - under + 1e-6, skip);
          if (floor > under) {
            s.y = floor;
            const gap = world.solidY0[i]! - floor;
            const fit = (t.standHeight - gap) / t.crouchDrop;
            if (fit > s.tuck) s.tuck = fit > 1 ? 1 : fit;
            h = capsuleHeight(t, s.tuck);
            moveResult.landed = 1;
            moveResult.impactSpeed = 0;
            s.vy = 0;
          } else {
            s.y = under;
            if (s.vy > 0) s.vy = 0;
          }
          moveResult.headHit = 1;
        } else if (dy < 0) {
          s.y = world.solidY1[i]!;
          s.vy = 0;
          moveResult.landed = 1;
          // An extension onto a surface lands with the mass's speed (nothing if the mass was rising).
          moveResult.impactSpeed = vyBefore < 0 ? -vyBefore : 0;
          shapeDy = 0;
        } else {
          // Rising: try a corner correction before stopping the jump.
          const overlapLeft = s.x + r - world.solidX0[i]!; // how far our right edge is past the solid's left edge
          const overlapRight = world.solidX1[i]! - (s.x - r); // how far our left edge is past the solid's right edge
          const cc = t.cornerCorrection;
          let nudged = false;
          if (overlapLeft <= cc && overlapLeft < overlapRight) {
            const nx = s.x - overlapLeft - 1e-6;
            if (world.overlapSolid(nx - r, s.y, nx + r, s.y + h, skip) < 0) {
              s.x = nx;
              nudged = true;
            }
          } else if (overlapRight <= cc) {
            const nx = s.x + overlapRight + 1e-6;
            if (world.overlapSolid(nx - r, s.y, nx + r, s.y + h, skip) < 0) {
              s.x = nx;
              nudged = true;
            }
          }
          if (nudged) {
            moveResult.cornerNudges++;
            continue;
          }
          s.y = world.solidY0[i]! - h;
          if (s.vy > 0) s.vy = 0;
          moveResult.headHit = 1;
        }
      }
    }
    // Horizontal
    if (s.vx !== 0) {
      const vxBefore = s.vx;
      const xBefore = s.x;
      s.x += s.vx * SUB_DT;
      let guard = 0;
      while (guard++ < 4) {
        const i = world.overlapSolid(s.x - r, s.y, s.x + r, s.y + h, skip);
        if (i < 0) break;
        const face = vxBefore > 0 ? world.solidX0[i]! - r : world.solidX1[i]! + r;
        // A face farther back than the step itself is not a wall the body ran into but a solid it was
        // already inside (a shape that could not be resolved above): stop where the step began rather
        // than jump to the far edge of a floor slab.
        const back = vxBefore > 0 ? xBefore - face : face - xBefore;
        s.x = back > r ? xBefore : face;
        if (s.vx !== 0) {
          moveResult.wallSolid = i;
          moveResult.wallSpeed = vxBefore < 0 ? -vxBefore : vxBefore;
        }
        s.vx = 0;
        moveResult.wallHit = 1;
      }
    }
  }
}

/** True when a solid top lies within `depth` below the feet. */
export function probeGround(world: World, s: SimState, t: CompiledTuning, depth: number): boolean {
  const r = t.capsuleRadius;
  const top = world.probeDown(s.x - r, s.x + r, s.y, depth);
  return top > Number.NEGATIVE_INFINITY && abs(top - s.y) <= depth;
}

/** Smallest tuck that fits under the ceiling above the feet (0 in the open). */
export function tuckFloor(world: World, s: SimState, t: CompiledTuning): number {
  const r = t.capsuleRadius;
  const room = world.roomAbove(s.x - r, s.x + r, s.y);
  if (room >= t.standHeight) return 0;
  const f = (t.standHeight - room) / t.crouchDrop;
  return f < 0 ? 0 : f > 1 ? 1 : f;
}
